import {mapObject} from '@benchling/js-utils';

import {MigrationObjectChange} from 'config-migration/import/import-changelog/MigrationChangelogConstants';
import {ImportObject, MigrationError, MigrationStatus} from 'config-migration/MigrationConstants';
import * as ImportUtil from 'util/ImportUtil';
import * as TaskRunner from 'util/TaskRunner';

import {transformObjectActionToServerRepresentation} from './import/planning-logic/transformPlannedActions';
import {LogMessageData} from './migration-log/Log';
import {DependencySummary} from './types/DependencySummary';
import {DestinationObjectResponse} from './types/DestinationObjectsResponse';
import {ParentActionsByObjectId} from './types/objectActions/ParentObjectAction';

// BNCH-70874: We are currently running an epic to improve performance, but in the interim, we want to ensure
//  that long running operations complete. The TaskRunner by default times out after ~11 minutes.
//  Because our operations can currently take up to 30 minutes (given our elevated Celery timeouts), we need
//    to override the defaults.
// TODO(BNCH-68125): Lower/remove this elevated timeout when we have our celery timeouts reduced.
const CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS: number = 1800000; // 30 minutes

export default {
  listConfigMigrationModels,
  exportConfigMigration,
  planConfigMigration,
  exportConfigMigrationDiff,
  validateConfigMigration,
  importConfigMigration,
  getConfigMigrationURL,
  uploadMigrationFile,
};

export interface ListConfigMigrationModelsResponse {
  dependencySummary: DependencySummary;
}

function listConfigMigrationModels(organizationId: string): Promise<ListConfigMigrationModelsResponse> {
  return TaskRunner.postJSONPromise({
    url: '/1/api/config-migration:list-models',
    data: {
      organizationId,
    },
  });
}

// Keep in sync with export task response in benchling/config_migration/response_objects/export/export_response.py
export interface ExportResponse {
  exportUrl: string;
  dependencies: Array<LogMessageData>;
  dependencySummary: DependencySummary;
  errors: Array<LogMessageData>;
  errorLogFilename: string;
  dependencyLogFilename: string;
  hasExternalFiles: boolean;
}

function exportConfigMigration(
  organizationId: string,
  apiIdsToExport: Array<string> | null
): Promise<ExportResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:export',
      data: {
        organizationId,
        apiIdsToExport,
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

// Keep in sync with benchling/config_migration/response_objects/plan/plan_config_migration_response.py
export interface PlanConfigMigrationResponse {
  importObjects: Array<ImportObject>;
  destinationObjects: DestinationObjectResponse;
}

function planConfigMigration(
  organizationId: string,
  importUrl: string
): Promise<PlanConfigMigrationResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:plan',
      data: {
        organizationId,
        importUrl,
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

// Keep in sync with benchling/config_migration/response_objects/diff/generate_diff_response.py
interface ConfigMigrationDiffResponse {
  url: string;
}

function exportConfigMigrationDiff(
  organizationId: string,
  importUrl: string
): Promise<ConfigMigrationDiffResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:diff',
      data: {
        organizationId,
        importUrl,
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

// Keep in sync with benchling/config_migration/response_objects/importer/perform_config_migration_response.py#PerformConfigMigrationSuccessResponseJson
export interface ImportSuccessResponse {
  status: MigrationStatus.SUCCESS;
  changelog: Array<MigrationObjectChange>;
  changelogUrl: string | null;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  unattributedErrorsBySource: {[unattributedErrorSource: string]: Array<MigrationError>};
}

// Keep in sync with benchling/config_migration/response_objects/importer/perform_config_migration_response.py#PerformConfigMigrationFailureResponseJson
export interface ImportErrorResponse {
  status: MigrationStatus.FAILURE;
  errorsByObjectId: {[objectId: string]: Array<MigrationError>};
  unattributedErrorsBySource: {[unattributedErrorSource: string]: Array<MigrationError>};
}

function validateConfigMigration(
  organizationId: string,
  importUrl: string,
  actionsByObjectId: ParentActionsByObjectId
): Promise<ImportSuccessResponse | ImportErrorResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:validate',
      data: {
        organizationId,
        importUrl,
        actionsByObjectId: mapObject(actionsByObjectId, transformObjectActionToServerRepresentation),
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

function importConfigMigration(
  organizationId: string,
  importUrl: string,
  importName: string | undefined,
  importDescription: string | undefined,
  actionsByObjectId: ParentActionsByObjectId
): Promise<ImportSuccessResponse | ImportErrorResponse> {
  return TaskRunner.postJSONPromise(
    {
      url: '/1/api/config-migration:import',
      data: {
        organizationId,
        importUrl,
        importName,
        importDescription,
        actionsByObjectId: mapObject(actionsByObjectId, transformObjectActionToServerRepresentation),
      },
    },
    CONFIG_MIGRATION_MAX_TOTAL_WAIT_TIME_IN_MS
  );
}

function uploadMigrationFile(file: File): JQueryPromise<string> {
  return ImportUtil.upload(file);
}

function getConfigMigrationURL(): string {
  return '/config-migration';
}
